export const colors = {
  primary: {
    50: "#fde8ee",
    200: "#f497af",
    300: "#ee688a",
    400: "#f03d66",
    500: "#e61d50",
    600: "#d11a49",
    700: "#b01a40",
    gradientStart: "#FF7BA2",
    gradientEnd: "#FF415D",
    shadow: "#67002299", // Used for gradients and inner shadows
  },
  primaryLight: {
    50: "#EBEBEF",
    100: "#E6E6EB",
    200: "#B3B3C2",
    300: "#81809A",
    400: "#35335D",
    500: "#030035",
  },
  neutral: {
    white: "#ffffff",
    100: "#f9fafc",
    200: "#f1f3f7",
    300: "#E9EEF4",
    400: "#dce0e7",
    500: "#d0d3db",
    600: "#b4b9c3",
    700: "#a4a9b1",
    800: "#838790",
    900: "#6a6e74",
  },
  background: {
    900: "#111114",
    850: "#1D212D",
    page404: "#0E111C", // 404 page base background
    page404GlowOverlay: "rgba(110, 121, 148, 0.2)", // 404 page gradient overlay
    800: "#1c1e20",
    700: "#2f3033",
    600: "#474a4e",
    menuDropdown: "#3E434A", // Action dropdown / menu panel background
    500: "#5f6265",
    overlay: "#D9DBFF0A",
    dragDrop: "#E0EFFF03", // rgba(224, 239, 255, 0.01)
    dragDropHover: "#E0EFFF08", // rgba(224, 239, 255, 0.03)
    navActive: "#2C2E30B3", // rgba(44, 46, 48, 0.7)
    buttonSecondary: "#444A5199", // rgba(68, 74, 81, 0.6)
    buttonClose: "#FFFFFF14", // rgba(255, 255, 255, 0.08)
    avatar: "#49515E", // rgba(73, 81, 94, 1)
    sidebarIcon: "#55586A66", // rgba(85, 88, 106, 0.4)
    search: "#8D99B726", // rgba(141, 153, 183, 0.15)
  },
  chip: {
    popular: "#e61d50",
    default: "#51545A",
  },
  text: {
    default: "#202223",
    500: "#ffffff",
    600: "#f1f3f7",
    700: "#e5e8ef",
    800: "#d8dce4",
    secondary: "#E2E6EE",
    disabled: "#838790", // Neutral/800-disabled-text
    tertiary: "#AEB5BF",
    whiteNeutral500: "#c9ced7",
    whiteNeutral600: "#989ea8",
    whiteNeutral800: "#313336",
    whiteNeutral900: "#0c0d0e",
  },
  success: {
    300: "#1FAD70",
  },
  error: {
    main: "#D44B58",
    400: "#EE1D52",
  },
  info: {
    main: "#0072E3",
  },
  warning: {
    main: "#F2CF51",
  },
  secondary: {
    100: "#ffffff",
    400: "#ced5de",
    500: "#898d94",
  },
  other: {
    black: "#000000",
    grey: "#d8d9dd",
    zinc400: "#a1a1aa",
    zincPlaceholder: "#f4f4f5",
  },
  accent: {
    pink: "#fd3264",
    cyan: "#6AF7F3", // rgba(106, 247, 243, 1)
    gradientStart: "#7EA7FF", // rgba(126, 167, 255, 1)
    gradientMid: "#9AE9FF", // rgba(154, 233, 255, 1)
    gradientEnd: "#30FFEA", // rgba(48, 255, 234, 1)
  },
  icon: {
    urlControlsDisabled: "#BABCBE",
    browserControls: "#3C4043",
    stroke: "#A4A9B1", // rgba(164, 169, 177, 1)
    strokeAlt: "#B4B9C3", // rgba(180, 185, 195, 1)
  },
  border: {
    divider: "#313336", // rgba(49, 51, 54, 1)
  },
  topBar: {
    bg: "#1A1D20",
    searchBg: "#2D3035",
    upgradeButtonBg: "#272A2E",
    avatarBg: "#3F4247",
    upgradeCyan: "#36D8E2",
    placeholder: "#8F949B",
    avatarInitials: "#E0E0E0",
  },
  modal: {
    background: "#1A1F2D",
    backgroundGradient:
      "linear-gradient(327deg, rgba(23, 23, 27, 0.25) 22.31%, rgba(101, 106, 121, 0.13) 117.52%), #1F2023",
    overlay: "#0F131ECC",
    boxShadowColor: "#00000014", // rgba(0, 0, 0, 0.08) - 8% black for modal shadow
  },
  tab: {
    activeBackground: "rgba(255,255,255,0.24)", // Active tab background
    inactiveBackground: "rgba(255,255,255,0.08)", // Inactive tab background
    activeHoverBackground: "rgba(255,255,255,0.28)", // Active tab hover background
    inactiveHoverBackground: "rgba(255,255,255,0.12)", // Inactive tab hover background
    inactiveTextOpacity: "rgba(180, 185, 195, 0.8)", // Inactive tab text with 80% opacity
  },
  dropdown: {
    background: "#3E434A", // Account dropdown background
    divider: "#55586A", // Divider line between header and menu items
    planBadgeBg: "#F2A7B8", // FREE plan badge background (pink)
    planBadgeText: "#1C1E20", // FREE plan badge text (dark on pink)
  },
  input: {
    background: "#8D99B726",
    clearButtonBg: "#FFFFFF1F",
  },
  button: {
    // Primary hover
    primaryHoverOverlay: "#FFFFFF0D",
    primaryHoverGlowOuter: "#FF6BA666",
    primaryHoverShadowDeep: "#F75A82CC",
    // Ghost gradient (unique values only, shared ones use primary.*)
    ghostGradientEnd: "#FF415DFA",
    // Ghost hover gradient
    ghostHoverGradientStart: "#F66385",
    ghostHoverGradientMid: "#FF3452FA",
    ghostHoverOverlay: "#D11A4933",
    // Ghost hover shadows
    ghostHoverGlowOuter: "#E5A1AC66",
    ghostHoverGlowMid: "#D54A61D9",
    ghostHoverShadowInner: "#FFDFEA80",
    // Outlined
    outlinedHoverBg: "#FFFFFF0A",
    outlinedHoverShadow: "#FFFFFF40",
    // Shared opacity values
    whiteLowOpacity: "#FFFFFF33", // 20% white - used for active states and shadows
    iconButtonHoverBg: "#FFFFFF1A", // 10% white - used for IconButton hover state
  },
  card: {
    contentBackground: "rgba(255,255,255,0.04)", // Tab content container background
    profileBackground: "rgba(240,244,248,0.05)", // Profile tab card background
    progressTrack: "rgba(120,120,120,0.2)", // Usage progress bar track background
  },
  overlay: {
    videoPlay: "rgba(255, 255, 255, 0.8)", // Video thumbnail play button background
    videoPlayActive: "rgba(255, 255, 255, 0.95)", // Video thumbnail play button hover background
    controlButton: "rgba(0, 0, 0, 0.45)", // Video control button background
    controlButtonHover: "rgba(0, 0, 0, 0.7)", // Video control button hover background
  },
  table: {
    rowHoverBackground: "rgba(237,241,246,0.07)", // Row hover background color
    headerBorder: "#595f64", // Header border color
  },
  skeleton: {
    thumbnailGradient: "linear-gradient(90deg, rgb(63,65,70) 0%, rgb(63,65,70) 30%, rgb(95,99,107) 42%, rgb(126,131,140) 50%, rgb(95,99,107) 58%, rgb(63,65,70) 70%, rgb(63,65,70) 100%)",
    pillGradient: "linear-gradient(90deg, rgb(63,65,70) 0%, rgb(63,65,70) 30%, rgb(95,99,107) 42%, rgb(126,131,140) 50%, rgb(95,99,107) 58%, rgb(63,65,70) 70%, rgb(63,65,70) 100%)",
  },
};

export const gradients = {
  primary: `radial-gradient(circle, ${colors.primary.gradientStart} 0%, ${colors.primary.gradientEnd} 98%)`,
  primaryHover: `linear-gradient(0deg, ${colors.button.primaryHoverOverlay} 0%, ${colors.button.primaryHoverOverlay} 100%), ${colors.primary[500]}`,
  primaryDisabled: `linear-gradient(135deg, ${colors.primary.shadow} 0%, ${colors.primary.shadow} 100%)`,
  innerShadow: `inset 3px 2px 12px 0 ${colors.primary.shadow}`,
  ghost: `radial-gradient(76.42% 33.98% at 50% 93.58%, ${colors.primary.gradientStart} 0%, ${colors.button.ghostGradientEnd} 100%)`,
  ghostHover: `radial-gradient(184.45% 48.58% at 50% 100%, ${colors.button.ghostHoverGradientStart} 1.05%, ${colors.button.ghostHoverGradientMid} 52.95%, ${colors.button.ghostHoverGradientMid} 100%), ${colors.button.ghostHoverOverlay}`,
  mainBackground: `linear-gradient(-35.16281520540052deg, rgba(55, 60, 74, 0.35) 3.521%, rgba(55, 60, 74, 0.25) 48.223%, rgba(141, 153, 183, 0.15) 85.296%)`,
  upgradeText: `linear-gradient(256.5808432791976deg, ${colors.accent.gradientStart} 4.6793%, ${colors.accent.gradientMid} 51.284%, ${colors.accent.gradientEnd} 115.72%)`,
};

export const shadows = {
  primaryHover: `0 0 15px 0 ${colors.button.primaryHoverGlowOuter}, 0 0 8px 0 ${colors.button.whiteLowOpacity} inset, 4px 4px 10px 0 ${colors.button.primaryHoverShadowDeep} inset`,
  ghostInner: `3px 2px 12px 0 ${colors.primary.shadow} inset`,
  upgradeText: `0px 0px 14px rgba(149, 230, 255, 0.5), 0px 0px 5px rgba(0, 0, 0, 0.4), 0px 0px 2px rgba(0, 0, 0, 0.25)`,
  dragDrop: `0px 0px 30px 0px #D0E9F326`,
  dragDropInner: `inset 0px 0px 20px 0px #D1F2FF26, inset 0px 0px 50px 0px #D1F2FF12`,
  sidebarIcon: `1px 1px 15px 0px rgba(0, 0, 0, 0.25)`,
  primaryButton: `0px 0px 50px 0px rgba(229, 161, 172, 0.2), 0px 0px 20px 0px rgba(213, 74, 97, 0.5)`,
  primaryButtonInner: `inset 3px 2px 20px 0px rgba(255, 223, 234, 0.5)`,
  buttonDisabledInner: `inset 3px 2px 12px 0px rgba(103, 0, 34, 0.6)`,
};

export default colors;
